'use client';

import CategoryCard from '@/app/(webapp)/_components/category-card';
import ContributorCarousel from '@/app/(webapp)/_components/contributor-carousel';
import MediaCarousel from '@/app/(webapp)/_components/media-carousel';
import UsageCard from '@/app/(webapp)/_components/usage-card';
import { partners } from '@/app/data/partners';
import { producers } from '@/app/data/producers';
import { StaticAdditionalPrograms, StaticMainPrograms } from '@/app/data/staticsPrograms';
import AppButton from '@/components/ui/app-button';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import Cta from '@/components/ui/cta';
import { IconChevron } from '@/components/ui/icons';
import { useUserData } from '@/context/auth/user-data-context';
import type { Category, Program } from '@/graphql/graphql';
import { LanguageIsoCode } from '@/graphql/graphql';
import { fetchCategoriesList } from '@/lib/api-categories';
import { fetchProgramsList } from '@/lib/api-programs';
import { isoEn } from '@/lib/utils';
import { StaticProgramsRail } from '@/types/staticProgram';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

type ProgramRail = Category & { programs: { data: Program[]; total: number } };
type ProgramsListResponse = { data: Program[]; total: number };

const renderSkeletons = () => {
    return (
        <div className="md:ml-18">
            <div className="animate-pulse bg-white/10 rounded h-8 w-64 mb-4" />
            <div className="hidden md:grid grid-cols-5 gap-4">
                {Array.from({ length: 5 }).map(() => (
                    <div
                        key={uuidv4()}
                        className="animate-pulse bg-white/10 rounded-[6px] aspect-3/4 w-full flex flex-col"
                    />
                ))}
            </div>

            <div className="grid md:hidden grid-cols-3 gap-4">
                {Array.from({ length: 3 }).map(() => (
                    <div
                        key={uuidv4()}
                        className="animate-pulse bg-white/10 rounded-[6px] aspect-3/4 w-full flex flex-col"
                    />
                ))}
            </div>
        </div>
    );
};

const renderCategoriesSkeleton = () => {
    return (
        <div className="mx-5 md:mx-18 pt-5 pb-10 md:pt-8 md:pb-20 gap-16">
            <div className="animate-pulse bg-white/10 rounded h-8 w-48 mb-5" />
            <div className="flex gap-4 overflow-hidden">
                {Array.from({ length: 6 }).map(() => (
                    <div key={uuidv4()} className="animate-pulse bg-white/10 rounded-[6px] w-32 h-40 flex-shrink-0" />
                ))}
            </div>
        </div>
    );
};

export default function Home() {
    const [programRails, setProgramRails] = useState<ProgramRail[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [latestPrograms, setLatestPrograms] = useState<ProgramsListResponse>({ data: [], total: 0 });

    // États de loading granulaires
    const [categoriesLoaded, setCategoriesLoaded] = useState(false);
    const [latestProgramsLoaded, setLatestProgramsLoaded] = useState(false);
    const [programRailsLoaded, setProgramRailsLoaded] = useState(false);

    const locale = useLocale().toLowerCase();

    const { isSeller, isBuyer, user, loading } = useUserData();
    const t = useTranslations();

    useEffect(() => {
        if (!loading) {
            const fetchData = async () => {
                const excludedGenres = process.env.NEXT_PUBLIC_EXCLUDED_GENRE_IDS
                    ? process.env.NEXT_PUBLIC_EXCLUDED_GENRE_IDS.replace(/\s+/g, '')
                          .split(',')
                          .map((id) => id.trim())
                    : [];
                const [categoriesRes, lProgramsRes] = await Promise.all([
                    fetchCategoriesList({ language: locale.toUpperCase() as LanguageIsoCode }),
                    fetchProgramsList({
                        pageSize: 15,
                        excludedGenres: excludedGenres,
                        sortOrder: 'desc',
                        sortBy: 'createdAt',
                        skipAuth: isSeller,
                    }),
                ]);

                setCategories(categoriesRes.data);
                setCategoriesLoaded(true);

                setLatestPrograms(lProgramsRes);
                setLatestProgramsLoaded(true);
                const rails: ProgramRail[] = [];
                for (const category of categoriesRes.data) {
                    const programs = await fetchProgramsList({
                        pageSize: 15,
                        excludedGenres: excludedGenres,
                        filters: { categoryIds: [category.identifier.id] },
                        sortOrder: 'random',
                        sortBy: 'firstBroadcastDate',
                        skipAuth: isSeller,
                    });

                    rails.push({ ...category, programs });
                    setProgramRails([...rails]);
                    setProgramRailsLoaded(true);
                }
            };

            fetchData();
        }
    }, [locale, loading, isSeller]);

    const renderLatestProgramsSection = () => {
        if (latestPrograms.total < 6) {
            return <></>;
        }
        return !latestProgramsLoaded ? (
            renderSkeletons()
        ) : (
            <MediaCarousel
                autoplay={true}
                mediaList={latestPrograms.data}
                title={t.rich('HomePage.sections.newProgramsTitle')}
                viewAllSrc="/programs"
                viewAllLabel={t('HomePage.sections.viewAllLabel')}
            />
        );
    };

    return (
        <main className="font-[family-name:var(--font-geist-sans)]">
            {/* <div className="absolute top-0 left-0 z-[-2] h-[100vh] w-[100%] bg-[url('/images/shade.png')] bg-center bg-cover bg-no-repeat" /> */}
            <div className="absolute top-0 left-0 z-[-1] h-[100vh] w-[100%]" />
            {/* Section Hero */}
            {!loading ? (
                <div className="items-center py-8 sm:pb-10 gap-16 sm:py-20">
                    <section className="flex flex-col gap-[32px] px-4">
                        <h1 className="gobold-title flex items-center justify-center uppercase text-white text-4xl md:text-6xl font-extrabold text-center px-4">
                            {t('HomePage.hero.titleRich.label1')}
                            <IconChevron className="w-[auto] h-[1em] mx-[10px]" />
                            {t('HomePage.hero.titleRich.label2')}
                        </h1>
                        <div className="text-xl md:text-2xl text-white text-center mx-auto max-w-[700px]">
                            {t('HomePage.hero.subtitle')}
                        </div>

                        <div className="flex flex-col md:flex-row justify-center">
                            {!user ? (
                                <AppButton
                                    customClassName="mb-5 md:mr-7 md:mb-0 w-full md:w-[auto]"
                                    innerText={t('HomePage.hero.primaryButton')}
                                    btnBgColor="#DD06AE"
                                    externalUrl="mailto:contact@bunqer.tech"
                                />
                            ) : undefined}
                            <AppButton
                                customClassName="mb-5 md:mb-0 w-full md:w-[auto]"
                                innerText={t('HomePage.hero.secondaryButton')}
                                borderWidth="1"
                                href="/programs"
                            />
                        </div>

                        {!isBuyer ? (
                            <div className="flex flex-col md:flex-row max-w-[1015px] items-stretch mt-5 md-mt-10 mx-auto">
                                <UsageCard
                                    className="flex-1 my-5 md:my-0"
                                    title={t('HomePage.process.step1.title')}
                                    text={t.rich('HomePage.process.step1.description', {
                                        highlight: (c: React.ReactNode) => <b>{c}</b>,
                                    })}
                                />
                                <IconChevron className="w-[48px] h-[48px] self-center mx-[18px] rotate-90 md:rotate-0" />
                                <UsageCard
                                    className="flex-1 my-5 md:my-0"
                                    title={t('HomePage.process.step2.title')}
                                    text={t.rich('HomePage.process.step2.description', {
                                        highlight: (c: React.ReactNode) => <b>{c}</b>,
                                    })}
                                />
                                <IconChevron className="w-[48px] h-[48px] self-center mx-[18px] rotate-90 md:rotate-0" />
                                <UsageCard
                                    className="flex-1 my-5 md:my-0"
                                    title={t('HomePage.process.step3.title')}
                                    text={t.rich('HomePage.process.step3.description', {
                                        highlight: (c: React.ReactNode) => <b>{c}</b>,
                                    })}
                                />
                            </div>
                        ) : undefined}
                    </section>
                </div>
            ) : undefined}

            {!isBuyer && !loading ? (
                <section className="flex flex-col items-center justify-center gap-[32px] mb-25">
                    <ContributorCarousel
                        title={t('HomePage.KeyPartners.title')}
                        contributorList={partners}
                        minQty={3}
                        customSpacing="30px"
                    />
                </section>
            ) : undefined}

            {/* Categories */}
            {!categoriesLoaded ? (
                renderCategoriesSkeleton()
            ) : (
                <div className="mx-5 md:mx-18 pt-5 pb-10 md:pt-8 md:pb-20 gap-16">
                    <section className="flex flex-col">
                        <h2 className="font-bold text-xl md:text-2xl mb-5">
                            {t('HomePage.sections.browseOfferTitle')}
                        </h2>

                        <Carousel
                            opts={{
                                align: 'start',
                                loop: true,
                                slidesToScroll: 1,
                            }}
                        >
                            <CarouselContent>
                                {categories.map((category) => {
                                    return (
                                        <CarouselItem
                                            key={category.identifier.sku}
                                            className={
                                                'basis-[calc(100%/2)] md:basis-[calc(100%/4)] lg:basis-[calc(100%/6)]'
                                            }
                                        >
                                            <CategoryCard
                                                href={{
                                                    pathname: '/programs',
                                                    query: { category: category.identifier.id },
                                                }}
                                                className="md:flex-1 mb-0 md:mb-5 mr-[10px] md:mr-0"
                                                name={category.titles[locale as LanguageIsoCode] as string}
                                                imageSrc={category.image}
                                            />
                                        </CarouselItem>
                                    );
                                })}
                            </CarouselContent>
                            <div className="hidden md:block absolute left-0 top-0 h-[100%] w-[95px]">
                                <CarouselPrevious
                                    variant="ghost"
                                    className="cursor-pointer hover:bg-transparent disabled:opacity-0"
                                />
                            </div>
                            <div className="hidden md:block absolute right-0 top-0 h-[100%] w-[95px]">
                                <CarouselNext
                                    variant="ghost"
                                    className="cursor-pointer hover:bg-transparent disabled:opacity-0"
                                />
                            </div>
                        </Carousel>
                    </section>
                </div>
            )}

            <div className="flex flex-col gap-[15px] md:gap-[72px] mb-[15px] md:mb-[72px]">
                {renderLatestProgramsSection()}
                {StaticMainPrograms.length > 0
                    ? StaticMainPrograms.map((rail: StaticProgramsRail) => {
                          return (
                              <MediaCarousel
                                  key={rail.id}
                                  mediaList={rail.programs as Program[]}
                                  title={rail.titles[locale]}
                                  viewAllSrc={{
                                      pathname: '/programs',
                                      query: { category: rail.categoryId },
                                  }}
                                  viewAllLabel={t('HomePage.sections.viewAllLabel')}
                              />
                          );
                      })
                    : undefined}
            </div>

            {/* Program Rails */}
            <div className="flex flex-col gap-[15px] md:gap-[72px] mb-[50px] md:mb-[110px]">
                {!programRailsLoaded
                    ? Array.from({ length: 3 }).map(() => <div key={uuidv4()}>{renderSkeletons()}</div>)
                    : programRails.map((rail) => {
                          if (rail.programs.total > 6) {
                              return (
                                  <MediaCarousel
                                      key={rail.identifier.id}
                                      mediaList={rail.programs.data}
                                      title={
                                          (rail.catchPhrases[locale as LanguageIsoCode] ??
                                              rail.catchPhrases[isoEn]) as string
                                      }
                                      viewAllSrc={{
                                          pathname: '/programs',
                                          query: { category: rail.identifier.id },
                                      }}
                                      viewAllLabel={t('HomePage.sections.viewAllLabel')}
                                  />
                              );
                          }
                      })}

                {StaticAdditionalPrograms.length > 0
                    ? StaticAdditionalPrograms.map((rail: StaticProgramsRail) => {
                          return (
                              <MediaCarousel
                                  key={rail.id}
                                  mediaList={rail.programs as Program[]}
                                  title={rail.titles[locale]}
                                  viewAllSrc={{
                                      pathname: '/programs',
                                      query: { category: rail.categoryId },
                                  }}
                                  viewAllLabel={t('HomePage.sections.viewAllLabel')}
                              />
                          );
                      })
                    : undefined}

                {!user && programRailsLoaded ? (
                    <Cta
                        title={t('HomePage.cta.title')}
                        ctaLabel={t('HomePage.cta.buttonLabel')}
                        externalUrl="mailto:contact@bunqer.tech"
                    />
                ) : undefined}
            </div>

            {/* Contributors */}
            {!loading ? (
                <div className="items-center justify-items-center gap-16 pb-15 md:pb-20 bg-[rgba(255,255,255,0.10)] overflow-hidden">
                    <section className="flex flex-col items-center justify-center gap-[32px]">
                        <ContributorCarousel
                            title={t('HomePage.Sellers.title')}
                            contributorList={producers}
                            minQty={10}
                        />
                    </section>
                </div>
            ) : undefined}
        </main>
    );
}
